import React from 'react';

import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import KeyboardArrowLeftOutlined from '@mui/icons-material/KeyboardArrowLeftOutlined';
import KeyboardArrowRightOutlined from '@mui/icons-material/KeyboardArrowRightOutlined';
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Typography } from '@mui/material';

import { useBoolean, useDebounce } from '@looko-corp/acloset-react-kit/src/react';

import { LookoButton } from '../Button/LookoButton';
import { HStack, VStack } from '../Stack';

export interface LookoNotificationModalProps {
  /**
   * 알림을 열거나 닫습니다.
   */
  open: boolean;

  /**
   * 알림 컨텐츠 리스트
   */
  contents: LookoNotificationModalContentProps[];

  /**
   * escape 버튼 또는 백드롭 클릭으로 인한 알림 닫기 요청을 비활성화 합니다.
   */
  disableEscape?: boolean;

  /**
   * hide backdrop
   */
  hideBackdrop?: boolean;

  /**
   * 알림 닫기를 요청할 때 불립니다.
   */
  onClose?: () => void;
}

export interface LookoNotificationModalContentProps {
  /**
   * 알림 제목.
   */
  title?: React.ReactNode;

  /**
   * 알림 내용.
   */
  subText?: React.ReactNode;

  /**
   * 알림 이미지.
   */
  imageUrl?: string;

  /**
   * 확인 버튼 텍스트.
   */
  confirmButtonText?: string;

  /**
   * 취소 버튼 텍스트.
   */
  cancelButtonText?: string;

  /**
   * 확인 버튼 이벤트.
   *
   * True 시에 알림을 닫습니다.
   */
  onConfirm?: () => boolean | Promise<boolean>;

  /**
   * 취소 버튼 이벤트.
   */
  onCancel?: () => void;

  /**
   * 다시 보지 않기 섹션
   */
  showAgainSection?: React.ReactNode;
}

/**
 * 알림 모달 기본 형태
 *
 * @see https://www.figma.com/file/PFrAvDaThtf8qMSRc9pNaV/Looko---Design-System?type=design&node-id=769-51373&mode=design&t=n8dWZCvcx0B2EPnw-0
 */
export const LookoNotificationModal = (props: LookoNotificationModalProps) => {
  const [currentIndex, setCurrentIndex] = React.useState(0);

  const currentContent = props.contents?.[currentIndex];

  const isMulti = (props.contents?.length ?? 0) > 1;

  const { value: isHandling, setValue: setHandling } = useBoolean(false);

  const handleCallback = useDebounce(async () => {
    setHandling(true);
    try {
      if (props.contents[currentIndex]?.onConfirm ? await props.contents[currentIndex]?.onConfirm?.() : true) {
        props.onClose?.();
      }
    } finally {
      setHandling(false);
    }
  });

  const onCancel = useDebounce(async () => {
    setHandling(true);
    try {
      if (props.contents[currentIndex]?.onCancel ? props.contents[currentIndex]?.onCancel?.() : true) {
        props.onClose?.();
      }
    } finally {
      setHandling(false);
    }
  });

  React.useLayoutEffect(() => {
    if (!props.open) {
      setHandling(false);
    }

    return () => setCurrentIndex(0);
  }, [props.open, setHandling]);

  React.useEffect(() => {
    if (currentIndex > props.contents.length - 1) {
      setCurrentIndex(props.contents.length - 1);
    }
  }, [props.contents]);

  return (
    <Dialog open={props.open} onClose={props.disableEscape ? undefined : props.onClose} maxWidth={'md'}>
      {props.contents.length > 1 && (
        <>
          <DialogTitle sx={{ p: 2 }}>
            <HStack justifyContent={'space-between'} flex={1} alignItems={'center'}>
              <IconButton
                onClick={() => setCurrentIndex(prev => prev - 1)}
                disabled={currentIndex === 0}
                size="small"
                sx={{ width: 24, height: 24 }}
              >
                <KeyboardArrowLeftOutlined sx={{ color: '#BDBDBD' }} />
              </IconButton>

              <HStack gap={0.5} width={'100%'} justifyContent={'center'}>
                {isMulti &&
                  Array.from({ length: props.contents.length }).map((v, index) => (
                    <FiberManualRecordIcon
                      key={index}
                      sx={{ width: 10, height: 10, color: currentIndex === index ? 'primary.main' : '#E1E4E8' }}
                    />
                  ))}
              </HStack>

              <IconButton
                onClick={() => setCurrentIndex(prev => prev + 1)}
                disabled={currentIndex === props.contents.length - 1}
                size="small"
                sx={{ width: 24, height: 24 }}
              >
                <KeyboardArrowRightOutlined sx={{ color: '#BDBDBD' }} />
              </IconButton>
            </HStack>
          </DialogTitle>
          <Divider />
        </>
      )}

      <DialogContent sx={{ p: 4 }}>
        <VStack gap={2} width={416}>
          <VStack gap={2} height={98} alignItems={'center'}>
            <VStack height={48} width={320}>
              {currentContent?.title != null &&
                (typeof currentContent?.title === 'string' ? (
                  <Typography variant="H2_Looko" style={{ whiteSpace: 'pre-line' }} textAlign={'center'}>
                    {currentContent?.title}
                  </Typography>
                ) : (
                  currentContent?.title
                ))}
            </VStack>

            <VStack height={34} width={320}>
              {currentContent?.subText != null &&
                (typeof currentContent?.subText === 'string' ? (
                  <Typography variant="Body_Looko" style={{ whiteSpace: 'pre-line' }} textAlign={'center'}>
                    {currentContent?.subText}
                  </Typography>
                ) : (
                  currentContent?.subText
                ))}
            </VStack>
          </VStack>

          {currentContent?.imageUrl != null && (
            <img src={currentContent?.imageUrl} style={{ width: 416, height: 312, objectFit: 'contain' }} />
          )}
        </VStack>
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 3, flex: 1 }}>
        <HStack justifyContent={'space-between'} alignItems={'center'} flex={1}>
          {props.contents[currentIndex]?.showAgainSection}

          <HStack justifyContent={'flex-end'} alignItems={'center'} gap={1}>
            {props.contents[currentIndex]?.confirmButtonText && (
              <LookoButton loading={isHandling} onClick={handleCallback} autoFocus color={'primary'}>
                {props.contents[currentIndex]?.confirmButtonText}
              </LookoButton>
            )}

            {props.contents[currentIndex]?.cancelButtonText && (
              <LookoButton onClick={onCancel} variant="outlined">
                {props.contents[currentIndex]?.cancelButtonText}
              </LookoButton>
            )}
          </HStack>
        </HStack>
      </DialogActions>
    </Dialog>
  );
};
